export interface CreateCheckoutRequest {
  id_cliente: number;
  idreserva: number;
  method_payment: 'pix' | 'credit_card';
  valor_total?: number;
  valor_bruto_original?: number;
  card_token?: string;
  tipo_reserva: string;
  voucher?: string; // opcional: código de voucher aplicado
  /**
   * Flag opcional para indicar se a reserva é individual.
   * Caso não seja informado pelo chamador, o serviço infere automaticamente
   * a partir de `tipo_reserva === 'individual'`.
   */
  reserva_individual?: boolean;
  installments?: number; // opcional: número de parcelas
  garantia_reagendamento?: { enabled: boolean }; // envia controle da garantia
  /**
   * Quando verdadeiro, sinaliza ao backend que deve GERAR uma nova cobrança
   * mesmo que exista uma anterior pendente. Útil para evitar reuso de QR expirado.
   */
  force_new?: boolean;
}

export interface CreateCheckoutResponse {
  status: 'success' | 'error' | 'pending' | 'processing';
  data?: {
    charge_id: string;
    order_id?: string;
    method: 'pix' | 'credit_card';
    /** Método de pagamento normalizado para consumo pelos modais */
    payment_method?: 'pix' | 'credit_card';
    pix?: import('../types/payment').PixInfo;
    transaction?: import('../types/payment').TransactionInfo;
    // Inclusões para permitir que o modal fixe totais corretamente
    resumo_pagamento?: import('../types/payment').ResumoPagamento;
    extras?: import('../types/payment').ExtrasResumo;
    totais?: import('../types/payment').TotaisResumo;
  };
  message?: string;
}

export interface QuoteRequest {
  id_cliente: number;
  idreserva?: number;
  idagenda?: number;
  tipo_reserva: string;
  voucher?: string; // opcional: código de voucher para prévia de descontos
  /**
   * Flag opcional para indicar se a reserva é individual.
   * Se ausente, será inferido no próprio serviço.
   */
  reserva_individual?: boolean;
  qtdpessoas?: number;
  valor_unitario?: number;
  valor_total?: number;
  /**
   * Objeto de pagamento.
   * A partir desta alteração, o campo correto é `payment_method`.
   * O campo `method` fica como compatibilidade retroativa e será descontinuado.
   */
  payment?: {
    /** Método de pagamento — use sempre `payment_method` para evitar conflito com o parâmetro de rota `method` no backend */
    payment_method?: 'pix' | 'credit_card';
    /** [DEPRECATED] compatibilidade com código legado — será convertido internamente para `payment_method` */
    method?: 'pix' | 'credit_card';
    installments?: number;
  };
  garantia_reagendamento?: { enabled: boolean }; // envia controle da garantia
}

export interface QuoteResponse {
  status: 'success' | 'error';
  data?: {
    resumo_pagamento: import('../types/payment').ResumoPagamento;
    // Campos adicionais para compatibilidade
    extras?: import('../types/payment').ExtrasResumo;
    totais?: import('../types/payment').TotaisResumo;
    descontos?: Record<string, unknown>;
    equipamentos?: Record<string, unknown>;
    garantia?: { valor?: number } | Record<string, unknown>;
    payment?: {
      /** Método de pagamento retornado. O backend pode enviar `method`; normalizamos e repassamos também como `payment_method` quando possível */
      method?: 'pix' | 'credit_card';
      payment_method?: 'pix' | 'credit_card';
      installments?: number;
    };
  };
  message?: string;
}

export interface InstallmentOptionsRequest {
  id_cliente: number;
  idreserva: number;
  /** Método de pagamento para opções de parcelamento. Renomeado para `payment_method` para evitar conflito com a rota `method` do backend */
  payment_method: 'pix' | 'credit_card';
  /** Valor total base para cálculo de taxa e totais (opcional, mas recomendado) */
  valor_total?: number;
  /** Quantidade de pessoas na reserva (opcional) */
  qtdpessoas?: number;
  /** Valor unitário por pessoa (opcional) */
  valor_unitario?: number;
  max_parcelas?: number;
  tipo_reserva: string;
  voucher?: string;
  reserva_individual?: boolean;
  garantia_reagendamento?: { enabled: boolean };
}

export interface InstallmentOption {
  parcelas: number;
  valor_parcela: number;
  valor_total: number;
  taxa: number;
  repassar: string;
  meta: import('../types/payment').TaxaMeta | Record<string, unknown>;
}

export interface InstallmentOptionsResponse {
  status: 'ok' | 'error';
  data?: {
    /** Método de pagamento utilizado para calcular as opções de parcelamento */
    method: 'pix' | 'credit_card';
    /** Campo normalizado para consumo na UI; espelho de `method` */
    payment_method?: 'pix' | 'credit_card';
    base_pagamento: number;
    garantia: number;
    base_com_garantia: number;
    options: InstallmentOption[];
  };
  message?: string;
}

export interface VoucherUsageResponse {
  status: 'ok' | 'error';
  data?: {
    voucher_code?: string | null;
    voucher_id?: number | null;
    idreserva: number;
    valor_base?: number;
    valor_desc?: number;
    usado_em?: string;
    meta?: Record<string, unknown>;
  } | null;
  message?: string;
}

export interface VoucherClearResponse {
  status: 'ok' | 'error';
  data?: { idreserva: number; cleared: boolean } | string;
  message?: string;
}

export interface VoucherLockedResponse {
  status: 'ok' | 'error';
  data?: { locked: boolean; reservas?: number[]; idvoucher?: number; reason?: string };
  message?: string;
}

// {|INICIO| 02/04/2026 às 10:05 - AJUSTES PARA LINT: REMOVER IMPORTS/VARIÁVEIS NÃO UTILIZADOS E PADRONIZAR CATCH}
// {|INICIO| 02/04/2026 às 16:32 - IMPORT: API_BASE_URL PARA FALLBACKS DE PING}
import { API_ENDPOINTS, AUTH_SEED, API_BASE_URL } from '../config/api';
// {|FIM| 02/04/2026 às 16:32 - IMPORT: API_BASE_URL PARA FALLBACKS DE PING}
// {|INICIO| 10/05/2026 às 10:30 - COMPATIBILIDADE SAFARI/iOS: TIMEOUT DE FETCH SEM AbortSignal.timeout}
import { fetchComTimeout } from '../utils/fetchTimeout';
// {|FIM| 10/05/2026 às 10:30 - COMPATIBILIDADE SAFARI/iOS: TIMEOUT DE FETCH SEM AbortSignal.timeout}
import { CheckoutMock } from '../mocks/checkoutMock';
import { montarValorAutorizacao } from '../utils/authToken';
import { PaymentValidator } from '../utils/paymentValidation';
import { PaymentLogger } from '../utils/paymentLogger';
import { obterBaseAplicacaoAtual } from '../utils/appBasePath';
import { logNetworkRequest, logNetworkResponse, logNetworkError } from '../utils/networkLogger';
import { devLog, devWarn, devError } from '../utils/devLog';

export class CheckoutService {
  private static backendPingCache: { lastOk: boolean; lastCheckAt: number } = { lastOk: false, lastCheckAt: 0 };
  // {|INICIO| 01/04/2026 às 01:02 - FLAGS FRONTEND RUNTIME VIA PING (LEITURA DE .ENV NO BACKEND)}
  private static frontendFlagsCache: { data: Record<string, unknown> | null; lastFetchAt: number } = { data: null, lastFetchAt: 0 };

  // {|INICIO| 22/07/2026 às 10:41 - ETAPA 3: PRIORIZAR BEARER DO CLIENTE NAS ROTAS DE RESERVA E CHECKOUT}
  /**
   * Function-level comment: Centraliza o header Authorization priorizando Bearer do cliente e mantendo fallback legado apenas quando necessário.
   */
  private static montarCabecalhosAutenticados(incluirJson = false): Record<string, string> {
    const cabecalhos: Record<string, string> = {
      'Authorization': montarValorAutorizacao(AUTH_SEED),
      'Accept': 'application/json'
    };

    if (incluirJson) {
      cabecalhos['Content-Type'] = 'application/json';
    }

    return cabecalhos;
  }
  // {|FIM| 22/07/2026 às 10:41 - ETAPA 3: PRIORIZAR BEARER DO CLIENTE NAS ROTAS DE RESERVA E CHECKOUT}

  // {|INICIO| 01/04/2026 às 14:49 - EXTRAIR FRONTEND_FLAGS DE RESPOSTAS ENCAPSULADAS (WRAPPER KGVT)}
  // Function-level comment: Normaliza a extração de `frontend_flags` considerando possíveis wrappers do backend.
  // Aceita formatos:
  // - { frontend_flags: {...} }
  // - { data: { frontend_flags: {...} } }
  // - { data: { checks: { frontend_flags: {...} } } }
  // - { checks: { frontend_flags: {...} } }
  private static extractFrontendFlags(raw: unknown): Record<string, unknown> | null {
    const asRecord = (v: unknown): Record<string, unknown> | null => {
      if (v && typeof v === 'object') return v as Record<string, unknown>;
      return null;
    };

    const root = asRecord(raw);
    if (!root) return null;

    const direct = asRecord(root.frontend_flags);
    if (direct) return direct;

    const data = asRecord(root.data);
    const fromData = data ? asRecord(data.frontend_flags) : null;
    if (fromData) return fromData;

    const checksFromData = data ? asRecord(data.checks) : null;
    const fromDataChecks = checksFromData ? asRecord(checksFromData.frontend_flags) : null;
    if (fromDataChecks) return fromDataChecks;

    const checks = asRecord(root.checks);
    const fromChecks = checks ? asRecord(checks.frontend_flags) : null;
    if (fromChecks) return fromChecks;

    return null;
  }
  // {|FIM| 01/04/2026 às 14:49 - EXTRAIR FRONTEND_FLAGS DE RESPOSTAS ENCAPSULADAS (WRAPPER KGVT)}

  // Function-level comment: Atualiza cache + expõe flags runtime em `globalThis` para utilitários (ex.: garantia.ts).
  private static setFrontendFlags(flags: Record<string, unknown> | null): void {
    this.frontendFlagsCache = { data: flags, lastFetchAt: Date.now() };
    try {
      (globalThis as any).__KGV_FRONTEND_FLAGS__ = flags ?? undefined;
    } catch {
      // noop
    }
  }
  // {|FIM| 01/04/2026 às 01:02 - FLAGS FRONTEND RUNTIME VIA PING (LEITURA DE .ENV NO BACKEND)}

  private static async isBackendAvailable(): Promise<boolean> {
    const now = Date.now();
    if (now - this.backendPingCache.lastCheckAt < 30000) {
      return this.backendPingCache.lastOk;
    }

    try {
      // {|INICIO| 23/07/2026 às 10:00 - AUTH: PRIORIZAR BEARER NO PING OPERACIONAL DO CHECKOUT}
      // Usar o endpoint de ping do checkout priorizando Bearer e preservando fallback compatível.
      // {|INICIO| 10/05/2026 às 10:30 - COMPATIBILIDADE SAFARI/iOS: SUBSTITUIR AbortSignal.timeout POR fetchComTimeout}
      const response = await fetchComTimeout(API_ENDPOINTS.checkout.ping, {
        method: 'GET',
        headers: { 'Authorization': montarValorAutorizacao(AUTH_SEED) },
      }, 4000);
      // {|FIM| 10/05/2026 às 10:30 - COMPATIBILIDADE SAFARI/iOS: SUBSTITUIR AbortSignal.timeout POR fetchComTimeout}
      // {|FIM| 23/07/2026 às 10:00 - AUTH: PRIORIZAR BEARER NO PING OPERACIONAL DO CHECKOUT}

      this.backendPingCache.lastCheckAt = now;

      if (response.ok) {
        // {|INICIO| 01/04/2026 às 01:02 - EXTRAIR FLAGS FRONTEND DO PING PARA CONFIG RUNTIME}
        try {
          const raw = await response.json().catch(() => null);
          // {|INICIO| 01/04/2026 às 14:49 - SUPORTAR RESP ENCAPSULADA {status,data:{...}}}
          const flags = this.extractFrontendFlags(raw);
          this.setFrontendFlags(flags);
          // {|FIM| 01/04/2026 às 14:49 - SUPORTAR RESP ENCAPSULADA {status,data:{...}}}
        } catch (_) {
          this.setFrontendFlags(null);
        }
        // {|FIM| 01/04/2026 às 01:02 - EXTRAIR FLAGS FRONTEND DO PING PARA CONFIG RUNTIME}
        this.backendPingCache.lastOk = true;
        return true;
      }

      // Alguns ambientes podem retornar 404/HTML; tentar inferir do corpo
      const text = await response.text().catch(() => '');
      const looksHealthy = /success|ok|available|disponivel|disponível/i.test(text);

      // Evitar falso negativo: em 404 assumimos disponível para deixar a chamada real decidir
      if (response.status === 404 || looksHealthy) {
        this.backendPingCache.lastOk = true;
        return true;
      }

      devWarn('Backend ping não-ok:', response.status, response.statusText);
      this.backendPingCache.lastOk = false;
      return false;
    } catch (erro) {
      // Em caso de erro de rede, não bloquear; permitir tentativa direta dos endpoints reais
      devWarn('Falha no ping do backend, permitindo tentativa direta:', erro);
      this.backendPingCache.lastCheckAt = now;
      this.backendPingCache.lastOk = true;
      return true;
    }
  }

  // {|INICIO| 01/04/2026 às 01:02 - ENDPOINT DE FLAGS FRONTEND RUNTIME (SEM REBUILD DO VITE)}
  static async getFrontendFlags(opcoes?: { forcarAtualizacao?: boolean }): Promise<Record<string, unknown> | null> {
    // Function-level comment: Busca flags de configuração do frontend no backend via ping.
    // Objetivo: permitir que mudanças em `.env` no servidor reflitam no frontend sem rebuild do bundle.
    const now = Date.now();
    // {|INICIO| 02/04/2026 às 12:44 - FLAGS: PERMITIR FORÇAR ATUALIZAÇÃO (PARÂMETROS TADMIN EM TEMPO REAL)}
    const forcarAtualizacao = Boolean(opcoes?.forcarAtualizacao);
    if (!forcarAtualizacao && now - this.frontendFlagsCache.lastFetchAt < 30000) return this.frontendFlagsCache.data;
    // {|FIM| 02/04/2026 às 12:44 - FLAGS: PERMITIR FORÇAR ATUALIZAÇÃO (PARÂMETROS TADMIN EM TEMPO REAL)}
    // {|INICIO| 02/04/2026 às 16:28 - BANNER: TENTATIVAS ADICIONAIS DE PING (kgvt, kgv, rest.php, ping.php)}
    try {
      const tentativas: Array<string> = [
        API_ENDPOINTS.checkout.ping,
        `${API_BASE_URL}/reserva_online_checkout_rest_service/ping`,
        `/kgvt/reserva_online_checkout_rest_service/ping`,
        `/kgv/reserva_online_checkout_rest_service/ping`,
        `${API_BASE_URL}/rest.php?class=reserva_online_checkout_rest_service&method=ping`,
        `/kgvt/rest.php?class=reserva_online_checkout_rest_service&method=ping`,
        `/kgv/rest.php?class=reserva_online_checkout_rest_service&method=ping`,
        `${API_BASE_URL}/ping.php`,
        `/kgvt/ping.php`,
        `/kgv/ping.php`,
      ];
      for (const url of tentativas) {
        try {
          // {|INICIO| 23/07/2026 às 10:00 - AUTH: PRIORIZAR BEARER NAS TENTATIVAS DE PING PARA FLAGS RUNTIME}
          // {|INICIO| 10/05/2026 às 10:30 - COMPATIBILIDADE SAFARI/iOS: SUBSTITUIR AbortSignal.timeout POR fetchComTimeout}
          const resp = await fetchComTimeout(url, {
            method: 'GET',
            headers: { 'Authorization': montarValorAutorizacao(AUTH_SEED) },
          }, 4000);
          // {|FIM| 10/05/2026 às 10:30 - COMPATIBILIDADE SAFARI/iOS: SUBSTITUIR AbortSignal.timeout POR fetchComTimeout}
          // {|FIM| 23/07/2026 às 10:00 - AUTH: PRIORIZAR BEARER NAS TENTATIVAS DE PING PARA FLAGS RUNTIME}
          if (!resp.ok) continue;
          const raw = await resp.json().catch(() => null);
          const flags = this.extractFrontendFlags(raw);
          if (flags && Object.keys(flags).length > 0) {
            this.setFrontendFlags(flags);
            return this.frontendFlagsCache.data;
          }
        } catch {
          // segue para próxima tentativa
        }
      }
      // Fallback adicional: tentar config_env.php (raiz do app SPA)
      try {
        // {|INICIO| 13/07/2026 às 16:21 - USAR BASE DA APLICACAO EM VEZ DO BASE_URL COMPILADO}
        const url = new URL(`${obterBaseAplicacaoAtual()}config_env.php`, window.location.origin);
        // {|FIM| 13/07/2026 às 16:21 - USAR BASE DA APLICACAO EM VEZ DO BASE_URL COMPILADO}
        url.searchParams.set('frontend_flags', '1');
        // {|INICIO| 23/07/2026 às 10:00 - AUTH: PRIORIZAR BEARER NO FALLBACK DE FLAGS VIA CONFIG_ENV}
        // {|INICIO| 10/05/2026 às 10:30 - COMPATIBILIDADE SAFARI/iOS: SUBSTITUIR AbortSignal.timeout POR fetchComTimeout}
        const fallbackResponse = await fetchComTimeout(url.toString(), {
          method: 'GET',
          headers: { 'Authorization': montarValorAutorizacao(AUTH_SEED) },
        }, 4000);
        // {|FIM| 10/05/2026 às 10:30 - COMPATIBILIDADE SAFARI/iOS: SUBSTITUIR AbortSignal.timeout POR fetchComTimeout}
        // {|FIM| 23/07/2026 às 10:00 - AUTH: PRIORIZAR BEARER NO FALLBACK DE FLAGS VIA CONFIG_ENV}
        if (fallbackResponse.ok) {
          const fallbackRaw = await fallbackResponse.json().catch(() => null);
          const fallbackFlags = this.extractFrontendFlags(fallbackRaw);
          this.setFrontendFlags(fallbackFlags);
        }
      } catch {
        void 0;
      }
      return this.frontendFlagsCache.data;
    } catch {
      this.setFrontendFlags(null);
      return null;
    }
    // {|FIM| 02/04/2026 às 16:28 - BANNER: TENTATIVAS ADICIONAIS DE PING (kgvt, kgv, rest.php, ping.php)}
  }
  // {|FIM| 01/04/2026 às 01:02 - ENDPOINT DE FLAGS FRONTEND RUNTIME (SEM REBUILD DO VITE)}

  // {|INICIO| 02/04/2026 às 13:15 - BANNER: ENDPOINT DEDICADO PARA CONFIG DO DESCONTO (TADMIN)}
  static async getProcessingFeeDiscountConfig(): Promise<Record<string, unknown> | null> {
    // Function-level comment: Busca no backend (TAdmin) os parâmetros do desconto na taxa de processamento.
    try {
      // {|INICIO| 10/05/2026 às 10:30 - COMPATIBILIDADE SAFARI/iOS: SUBSTITUIR AbortSignal.timeout POR fetchComTimeout}
      const response = await fetchComTimeout(API_ENDPOINTS.checkout.processingFeeDiscountConfig, {
        method: 'GET',
        headers: { 'Authorization': `Basic ${AUTH_SEED}` },
      }, 4000);
      // {|FIM| 10/05/2026 às 10:30 - COMPATIBILIDADE SAFARI/iOS: SUBSTITUIR AbortSignal.timeout POR fetchComTimeout}
      if (!response.ok) return null;
      const raw = await response.json().catch(() => null);
      if (!raw || typeof raw !== 'object') return null;
      const root = raw as Record<string, unknown>;
      const data = (root.data && typeof root.data === 'object') ? (root.data as Record<string, unknown>) : null;
      return data ?? root;
    } catch {
      return null;
    }
  }
  // {|FIM| 02/04/2026 às 13:15 - BANNER: ENDPOINT DEDICADO PARA CONFIG DO DESCONTO (TADMIN)}

  static async createPayment(request: CreateCheckoutRequest): Promise<CreateCheckoutResponse> {
    /**
     * Cria a cobrança no backend (PIX ou Cartão).
     * - Reenvia dados essenciais da reserva e método de pagamento.
     * - Inclui `reserva_individual` para permitir regras de desconto no backend.
     * - Normaliza e retorna o objeto de resposta do checkout.
     */
    const startTime = Date.now();
    const reservationId = `res_${request.idreserva}_${Date.now()}`;
    
    PaymentLogger.logPaymentStart({
      reservationId,
      paymentMethod: request.method_payment,
      amount: typeof request.valor_total === 'number' ? request.valor_total : 0,
      userId: request.id_cliente.toString()
    });
    
    const backendAvailable = await this.isBackendAvailable();
    
    if (!backendAvailable) {
      PaymentLogger.logBackendResponse({
        reservationId,
        endpoint: 'createPayment',
        statusCode: 0,
        success: false,
        responseTime: Date.now() - startTime,
        error: 'Backend não disponível'
      });
      
      return { status: 'error', message: 'Falha ao comunicar com backend' };
    }

    // Construir o payload da requisição
    const requestBody: Record<string, unknown> = {
      id_cliente: request.id_cliente,
      idreserva: request.idreserva,
      method_payment: request.method_payment,
      tipo_reserva: request.tipo_reserva,
      reserva_individual: (typeof request.reserva_individual === 'boolean')
        ? request.reserva_individual
        : (request.tipo_reserva === 'individual')
    };

    // {|INICIO| 29/09/2026 às 14:36 - RESTAURAR BLOCO createPayment IDENTICO AO BACKUP reservaonline_25_09_2025}
    // Versao anterior (corrompida/sintaxe incompleta): faltava sanitização de campos bloqueados (HTTP 422),
    // faltavam atribuições condicionais de card_token/voucher/installments/garantia_reagendamento/force_new,
    // e os headers estavam errados. Com base no diff do backup 25/09, blocos abaixo restaurados integralmente.
    // {|INICIO| 25/09/2026 às 16:20 - INSTRUMENTACAO + PROTECAO: REMOVER CAMPOS FINANCEIROS BLOQUEADOS E LOGAR}
    // Causa raiz confirmada: envio de 'valor_total' ou 'valor_bruto_original' no payload ACIONA o bloqueio
    // cego (HTTP 422) do backend /kgv (produção ainda não recebeu patch do bloqueio_inteligente do /kgvt).
    // Solução: REMOVER explicitamente os campos financeiros do requestBody, mesmo que alguém passe
    // acidentalmente. Também disparar log de auditoria para client_log.php com dados de diagnóstico.
    const camposBloqueadosBackend = ['valor_total', 'valor_bruto_original', 'valor_base', 'valor_unitario', 'preco_unitario', 'qtdpessoas', 'desconto', 'taxa', 'juros', 'valor_parcela', 'total_parcelado'];
    const logPayloadSanitizacao: Record<string, unknown> = {};
    for (const campoBloqueado of camposBloqueadosBackend) {
      // {|INICIO| 29/09/2026 às 14:44 - CORRIGIR CAST CreateCheckoutRequest → Record (TS diagnostic: "Neither type sufficiently overlaps")}
      // Backup 25/09 usava `(request as Record<string, unknown>)` diretamente; o TS 5.6 rigoroso acusa warning.
      // Usar unknown intermediario e tipar requestRaw uma unica vez, evita warning.
      const requestRaw = request as unknown as Record<string, unknown>;
      if (requestRaw[campoBloqueado] !== undefined) {
        logPayloadSanitizacao[campoBloqueado] = requestRaw[campoBloqueado];
        delete requestRaw[campoBloqueado];
      }
      // {|FIM| 29/09/2026 às 14:44 - CORRIGIR CAST CreateCheckoutRequest → Record}
    }
    try {
      const logSanitizacao = Object.keys(logPayloadSanitizacao).length > 0 ? logPayloadSanitizacao : 'nenhum_campo_financeiro_enviado';
      void fetch('client_log.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          level: 'info',
          event: 'CHECKOUTCREATE_PAYLOAD_SANITIZADO',
          message: 'CreatePayment payload pre-processado',
          data: {
            idreserva: request.idreserva,
            method_payment: request.method_payment,
            force_new_solicitado: request.force_new ?? false,
            campos_financeiros_removidos: logSanitizacao,
            endpoint: API_ENDPOINTS.checkout.checkoutCreate,
            api_base_url: API_BASE_URL
          }
        })
      }).catch(() => { void 0; });
    } catch { void 0; }
    // {|FIM| 25/09/2026 às 16:20 - INSTRUMENTACAO + PROTECAO: REMOVER CAMPOS FINANCEIROS BLOQUEADOS E LOGAR}

    // Adicionar token do cartão se fornecido
    if (request.card_token) {
      requestBody.card_token = request.card_token;
    }

    // Adicionar voucher se fornecido
    if (request.voucher) {
      requestBody.voucher = request.voucher;
    }

    // Adicionar número de parcelas se fornecido
    if (request.installments) {
      requestBody.installments = request.installments;
    }

    // Adicionar garantia de reagendamento se fornecida
    if (request.garantia_reagendamento) {
      requestBody.garantia_reagendamento = request.garantia_reagendamento;
    }

    // Forçar nova cobrança quando solicitado (evitar reuso de QR expirado)
    if (request.force_new === true) {
      requestBody.force_new = true;
    }

    const endpoint = API_ENDPOINTS.checkout.checkoutCreate;
    const responseTime = Date.now() - startTime;

    devLog('📤 Enviando pagamento para backend', { endpoint, body: { ...requestBody, card_token: requestBody.card_token ? '****' : undefined } });

    const headers = this.montarCabecalhosAutenticados(true);

    try {
      // {|INICIO| 10/05/2026 às 10:30 - COMPATIBILIDADE SAFARI/iOS: SUBSTITUIR AbortSignal.timeout POR fetchComTimeout}
      const response = await fetchComTimeout(endpoint, {
        method: 'POST',
        headers,
        body: JSON.stringify(requestBody),
      }, 45000);
      // {|FIM| 10/05/2026 às 10:30 - COMPATIBILIDADE SAFARI/iOS: SUBSTITUIR AbortSignal.timeout POR fetchComTimeout}
      // {|FIM| 29/09/2026 às 14:36 - RESTAURAR BLOCO createPayment IDENTICO AO BACKUP reservaonline_25_09_2025}
      
      PaymentLogger.logBackendResponse({
        reservationId,
        endpoint,
        statusCode: response.status,
        success: response.ok,
        responseTime
      });

      if (!response.ok) {
        // Log da resposta de erro
        PaymentLogger.logBackendResponse({
          reservationId,
          endpoint,
          statusCode: response.status,
          success: false,
          responseTime,
          error: `HTTP ${response.status}: ${response.statusText}`
        });
        
        // Tentar ler o corpo da resposta para mais detalhes do erro
        try {
          const text = await response.text();
          return { status: 'error', message: text };
        } catch {
          return { status: 'error', message: `HTTP ${response.status}: ${response.statusText}` };
        }
      }

      // Normalizar resposta
      const raw = await response.json();
      return this.normalizeCheckoutResponse(raw as unknown);
    } catch (erro) {
      devWarn('Erro ao criar pagamento:', erro);
      return { status: 'processing', message: 'Falha ao comunicar com backend' };
    }
  }

  /**
   * Normaliza a resposta do backend para o formato esperado
   */
  static normalizeCheckoutResponse(raw: unknown): CreateCheckoutResponse {
    try {
      const root: any = raw || {};
      const inner: any = root.data || {};
      // Comentário (function-level): Alguns backends passaram a encapsular dados
      // sob `payment` dentro de `data`. Aqui extraímos `payment` para maior robustez.
      const payment: any = inner.payment || root.payment || inner?.data?.payment || {};

      // Determinar status
      let status: CreateCheckoutResponse['status'] = 'processing';
      const statusCandidates = [
        root.status,
        inner.status,
        inner.charge_status,
        inner?.data?.status,
        inner?.data?.charge_status
      ];
      const normalizeStatus = (s?: string): CreateCheckoutResponse['status'] => {
        if (!s) return 'processing';
        const val = String(s).toLowerCase();
        if (val === 'success' || val === 'pending' || val === 'processing') return val as any;
        if (val === 'paid') return 'success';
        if (val === 'failed' || val === 'error' || val === 'canceled' || val === 'chargedback') return 'error';
        return 'processing';
      };
      for (const s of statusCandidates) {
        const norm = normalizeStatus(typeof s === 'string' ? s : undefined);
        // Priorizar status mais específicos
        if (norm !== 'processing' || (typeof s === 'string' && (s === 'pending' || s === 'processing'))) {
          status = norm;
          break;
        }
      }

      // Extrair IDs
      const chargeId = (
        (payment as any).charge_id || (inner as any).charge_id || (root as any).charge_id || (inner as any)?.data?.charge_id || (root as any)?.data?.charge_id ||
        (payment as any).transaction?.id || (inner as any).transaction?.id || (inner as any).id || ''
      );
      const orderId = (payment as any).order_id || (inner as any).order_id || (root as any).order_id || (inner as any)?.data?.order_id || (root as any)?.data?.order_id;

      // Determinar método de pagamento
      const method: 'pix' | 'credit_card' | undefined =
        (payment as any).method || (payment as any).payment_method ||
        (inner as any).method || (inner as any)?.data?.method || (inner as any).method_payment || (inner as any)?.data?.method_payment ||
        (((payment as any).pix || (inner as any).pix || (inner as any)?.data?.pix) ? 'pix' : (((payment as any).transaction || (inner as any).transaction || (inner as any)?.data?.transaction) ? 'credit_card' : undefined));

      // Resolver dados PIX
      const resolvePix = (obj: unknown): import('../types/payment').PixInfo | undefined => {
        if (!obj || typeof obj !== 'object') return undefined;
        const o = obj as Record<string, unknown>;
        const qrCode = (o as any).qr_code || (o as any).qrCode || (o as any).qrcode || (o as any).qr_image || (o as any).qr || (o as any).imagem_qrcode || '';
        const qrText = (o as any).qr_code_text || (o as any).qrCodeText || (o as any).qrcode_text || (o as any).qrcodeText || (o as any).qr_text || '';
        const expires = (o as any).expires_at || (o as any).expiresAt || (o as any).expira_em || '';
        if (qrCode || qrText) {
          return {
            qr_code: String(qrCode || ''),
            qr_code_text: String(qrText || ''),
            expires_at: String(expires || '')
          };
        }
        return undefined;
      };
      const pixCandidates = [
        payment?.pix,
        inner?.pix,
        root?.pix,
        inner?.data?.pix,
        root?.data?.pix,
        resolvePix(payment),
        resolvePix(inner?.data),
        resolvePix(root?.data),
        resolvePix(payment?.transaction),
        resolvePix(inner?.transaction),
        resolvePix(root?.transaction),
        resolvePix(inner),
        resolvePix(root)
      ];
      const firstPix = pixCandidates.find(p => p && (p.qr_code || p.qr_code_text));
      const pix = firstPix || (inner.transaction?.qr_code || inner.transaction?.qr_code_text ? {
        qr_code: inner.transaction?.qr_code || '',
        qr_code_text: inner.transaction?.qr_code_text || '',
        expires_at: inner.transaction?.expires_at || ''
      } : undefined);

      // Resolver dados de transação
      const transaction = (payment as any).transaction || (inner as any).transaction || (root as any).transaction || (inner as any)?.data?.transaction || (root as any)?.data?.transaction;

      // Campos extras para compatibilidade
      const resumo_pagamento = (payment as any).resumo_pagamento || (inner as any).resumo_pagamento || (root as any).resumo_pagamento || (inner as any).resumo || (root as any).resumo || undefined;
      const extras = (resumo_pagamento as any)?.extras || (payment as any).extras || (inner as any).extras || (root as any).extras || undefined;
      const totais = (payment as any).totais || (inner as any).totais || (root as any).totais || undefined;

      const normalized: CreateCheckoutResponse = {
        status,
        data: {
          charge_id: chargeId,
          order_id: orderId,
          method: method as any,
          payment_method: method as any,
          pix: pix as any,
          transaction: transaction as any,
          // incluir campos usados pela UI para fixar totais
          resumo_pagamento: resumo_pagamento as any,
          extras: extras as any,
          totais: totais as any
        },
        message: (root as any).message || (inner as any).message
      };

      // Adicionar campos extras para compatibilidade
      (normalized as any).resumo_pagamento = resumo_pagamento as any;
      (normalized as any).extras = extras as any;
      (normalized as any).totais = totais as any;

      return normalized;
    } catch (e) {
      devWarn('normalizeCheckoutResponse: falha ao normalizar resposta', e);
      return { status: 'processing', message: 'Estrutura inesperada na resposta do checkout' };
    }
  }

  /**
   * Obtém voucher aplicado previamente à reserva (treserva_voucher_uso).
   * Uso: preencher automaticamente o campo de voucher no Checkout KGV ao abrir.
   */
  static async getVoucherUsage(idreserva: number): Promise<VoucherUsageResponse> {
    try {
      const endpoint = API_ENDPOINTS.checkout.voucherUsage;
      const headers = {
        'Authorization': `Basic ${AUTH_SEED}`,
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      };
      // Log de requisição para diagnóstico (somente em desenvolvimento)
      if ((import.meta as any)?.env?.DEV === true) {
        devLog('📤 Enviando resgate de voucher', { endpoint, body: { idreserva } });
      }
      logNetworkRequest({ endpoint: 'voucherUsage', method: 'POST', url: endpoint, payload: { idreserva }, headers });
      const response = await fetch(endpoint, {
        method: 'POST',
        headers,
        body: JSON.stringify({ idreserva })
      });
      if (!response.ok) {
        const message = await response.text().catch(() => `HTTP ${response.status}`);
        logNetworkError({ endpoint: 'voucherUsage', method: 'POST', url: endpoint, status: response.status, error: new Error(message) });
        // Fallback GET
        try {
          const qs = new URLSearchParams({ idreserva: String(idreserva) }).toString();
          const getUrl = `${endpoint}?${qs}`;
          devWarn('⚠️ voucherUsage POST falhou, tentando GET', { status: response.status, getUrl });
          logNetworkRequest({ endpoint: 'voucherUsage', method: 'GET', url: getUrl, payload: undefined, headers: { 'Authorization': `Basic ${AUTH_SEED}`, 'Accept': 'application/json' } });
          const getResp = await fetch(getUrl, {
            method: 'GET',
            headers: { 'Authorization': `Basic ${AUTH_SEED}`, 'Accept': 'application/json' }
          });
          if (getResp.ok) {
            const getRaw = await getResp.json();
      if ((import.meta as any)?.env?.DEV === true) {
        devLog('📥 Resposta voucherUsage GET (raw)', getRaw);
      }
            logNetworkResponse({ endpoint: 'voucherUsage', method: 'GET', url: getUrl, status: getResp.status, body: getRaw });
            const normalizedGet = getRaw?.data?.data ? getRaw.data : getRaw;
            return normalizedGet as VoucherUsageResponse;
          } else {
            const getMsg = await getResp.text().catch(() => `HTTP ${getResp.status}`);
            logNetworkError({ endpoint: 'voucherUsage', method: 'GET', url: getUrl, status: getResp.status, error: new Error(getMsg) });
            return { status: 'error', message: message };
          }
        } catch {
          return { status: 'error', message };
        }
      }
      const raw = await response.json();
      if ((import.meta as any)?.env?.DEV === true) {
        devLog('📥 Resposta voucherUsage (raw)', raw);
      }
      logNetworkResponse({ endpoint: 'voucherUsage', method: 'POST', url: endpoint, status: 200, body: raw });
      const normalized = raw?.data?.data ? raw.data : raw;
      if ((import.meta as any)?.env?.DEV === true) {
        devLog('📥 Resposta voucherUsage (normalizada)', normalized);
      }
      return normalized as VoucherUsageResponse;
    } catch (erro) {
      logNetworkError({ endpoint: 'voucherUsage', method: 'POST', url: API_ENDPOINTS.checkout.voucherUsage, status: 0, error: erro instanceof Error ? erro : new Error(String(erro)) });
      return { status: 'error', message: erro instanceof Error ? erro.message : 'Erro ao obter voucher da reserva' };
    }
  }

  /**
   * Remove o registro de uso de voucher para a reserva.
   * Uso: botão Remover no Checkout KGV.
   */
  static async clearVoucherUsage(idreserva: number): Promise<VoucherClearResponse> {
    try {
      const endpoint = API_ENDPOINTS.checkout.voucherClear;
      const headers = {
        'Authorization': `Basic ${AUTH_SEED}`,
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      };
      if ((import.meta as any)?.env?.DEV === true) {
        devLog('🧹 Solicitando limpeza de voucher', { endpoint, body: { idreserva } });
      }
      const response = await fetch(endpoint, { method: 'POST', headers, body: JSON.stringify({ idreserva }) });
      if (!response.ok) {
        const message = await response.text().catch(() => `HTTP ${response.status}`);
        return { status: 'error', message };
      }
      const raw = await response.json();
      if ((import.meta as any)?.env?.DEV === true) {
        devLog('✅ Limpeza de voucher (raw)', raw);
      }
      const normalized = raw?.data?.data ? raw.data : raw;
      return normalized as VoucherClearResponse;
    } catch (e) {
      return { status: 'error', message: e instanceof Error ? e.message : 'Erro ao limpar voucher da reserva' };
    }
  }

  /**
   * Verifica se o voucher está travado por uso em outra reserva do mesmo cliente.
   */
  static async isVoucherLocked(idcliente: number, codigo: string, idreserva?: number): Promise<VoucherLockedResponse> {
    try {
      const endpoint = API_ENDPOINTS.checkout.voucherLocked;
      const headers = {
        'Authorization': `Basic ${AUTH_SEED}`,
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      };
      const response = await fetch(endpoint, { method: 'POST', headers, body: JSON.stringify({ idcliente, codigo, idreserva }) });
      if (!response.ok) {
        const message = await response.text().catch(() => `HTTP ${response.status}`);
        return { status: 'error', message };
      }
      const raw = await response.json();
      const normalized = raw?.data?.data ? raw.data : raw;
      return normalized as VoucherLockedResponse;
    } catch (e) {
      return { status: 'error', message: e instanceof Error ? e.message : 'Erro ao verificar bloqueio de voucher' };
    }
  }

  /**
   * Solicita cotação de pagamento
   */
  static async quote(request: QuoteRequest): Promise<QuoteResponse> {
    /**
     * Solicita uma prévia (cotação) ao backend.
     * - Repassa `reserva_individual` (inferido quando necessário) para cálculo correto
     *   de descontos, taxas e garantia.
     * - No fluxo ativo da aplicação, exige `idreserva` e falha fechado quando o backend
     *   não consegue responder com a cotação oficial.
     */
    const backendAvailable = await this.isBackendAvailable();
    // {|INICIO| 02/04/2026 às 09:16 - EVITAR QUALQUER CACHE APÓS AJUSTE DE CONFIGURAÇÃO NO ADMIN}
    // Function-level comment: Força recálculo sempre que a cotação for requisitada,
    // evitando caches de intermediários ao anexar um carimbo de tempo na URL
    // e instruindo o navegador a não armazenar a resposta.
    const endpoint = `${API_ENDPOINTS.checkout.quote}?_=${Date.now()}`;
    const headers = {
      ...this.montarCabecalhosAutenticados(true),
      'Content-Type': 'application/json',
      'Cache-Control': 'no-store, no-cache, must-revalidate',
      'Pragma': 'no-cache'
    };
    // {|FIM| 02/04/2026 às 09:16 - EVITAR QUALQUER CACHE APÓS AJUSTE DE CONFIGURAÇÃO NO ADMIN}

    // Inferir de forma resiliente se a reserva é individual
    const isIndividual = (typeof request.reserva_individual === 'boolean')
      ? request.reserva_individual
      : (request.tipo_reserva === 'individual');

    const body: Record<string, unknown> = {
      id_cliente: request.id_cliente,
      tipo_reserva: request.tipo_reserva,
      // Backend usa esta flag para aplicar desconto feminino automático, etc.
      reserva_individual: isIndividual
    };

    // Adicionar voucher quando presente
    if (request.voucher) {
      body.voucher = request.voucher;
    }

    // Adicionar campos opcionais
    if (typeof request.idreserva === 'number' && request.idreserva > 0) {
      body.idreserva = request.idreserva;
    } else {
      return { status: 'error', message: 'idreserva inválido' };
    }

    // Adicionar informações de pagamento
    if (request.payment) {
      // Normaliza o método: prioriza `payment_method`, aceita `method` por compatibilidade
      const payMethod = request.payment.payment_method ?? request.payment.method;
      body.payment = {
        method: payMethod,
        payment_method: payMethod,
        installments: typeof request.payment.installments === 'number' ? request.payment.installments : 1
      };
      // Alias plano que o backend espera (sem conflitar com o parâmetro de rota `method`)
      body.method_payment = payMethod;
      body.installments = typeof request.payment.installments === 'number' ? request.payment.installments : 1;
    }

    // Adicionar garantia de reagendamento se fornecida
    if (request.garantia_reagendamento) {
      body.garantia_reagendamento = request.garantia_reagendamento;
    }

    // Log request -> arquivo (INFO pode ser filtrado por env no servidor)
    try {
      await fetch('client_log.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ level: 'info', event: 'QUOTE_REQUEST', message: 'Enviando cotação', data: { id_cliente: body.id_cliente, tipo_reserva: body.tipo_reserva, payment: body.payment, method_payment: body.method_payment, installments: body.installments, garantia_reagendamento: body.garantia_reagendamento } })
      });
    } catch {}

    if (!backendAvailable) {
      devWarn('quote(): backend indisponível; cotação oficial não pôde ser obtida');
      try {
        await fetch('client_log.php', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ level: 'warn', event: 'QUOTE_BACKEND_UNAVAILABLE', message: 'Ping indisponível', data: { idreserva: body.idreserva } })
        });
      } catch {}
      return { status: 'error', message: 'Erro ao calcular cotação' };
    }

    try {
      if ((import.meta as any)?.env?.DEV === true) {
        devLog('📤 Enviando cotação para backend', { endpoint, body });
      }
      // {|INICIO| 10/05/2026 às 10:30 - COMPATIBILIDADE SAFARI/iOS: SUBSTITUIR AbortSignal.timeout POR fetchComTimeout}
      const response = await fetchComTimeout(endpoint, {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
      }, 8000);
      // {|FIM| 10/05/2026 às 10:30 - COMPATIBILIDADE SAFARI/iOS: SUBSTITUIR AbortSignal.timeout POR fetchComTimeout}

      if (!response.ok) {
        const message = await response.text();
        try {
          await fetch('client_log.php', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ level: 'error', event: 'QUOTE_HTTP_ERROR', message, data: { status: response.status } })
          });
        } catch {}
        return { status: 'error', message };
      }

      // Tentar parsear JSON
      let data: any;
      try {
        data = await response.clone().json();
      } catch {
        const text = await response.text();
        const trimmed = text.trim();
        const start = trimmed.indexOf('{');
        const end = trimmed.lastIndexOf('}');
        if (start !== -1 && end !== -1 && end > start) {
          data = JSON.parse(trimmed.slice(start, end + 1));
        } else {
          devWarn('quote(): resposta não-JSON do backend');
          try {
            await fetch('client_log.php', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ level: 'warn', event: 'QUOTE_NON_JSON', message: 'Resposta não JSON', data: { text: trimmed.slice(0, 200) } })
            });
          } catch {}
          return { status: 'error', message: 'Erro ao calcular cotação' };
        }
      }

      // Interpretar status; tratar 'ok' e 'success' como sucesso, e apenas estados explícitos de erro como erro
      const normalizedStatus = typeof data?.status === 'string' ? data.status.toLowerCase() : '';
      if (normalizedStatus && ['error', 'erro', 'failed'].includes(normalizedStatus)) {
        const msg = typeof data?.data === 'string' ? data.data : 'Erro ao calcular cotação';
        try {
          await fetch('client_log.php', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ level: 'error', event: 'QUOTE_BACKEND_ERROR', message: msg, data })
          });
        } catch {}
        return { status: 'error', message: msg };
      }

      // Normalizar dados da resposta
      // Observação:
      // Alguns endpoints retornam estrutura aninhada: { status, data: { resumo_pagamento, totais, extras, ... } }
      // Outros retornam diretamente { resumo_pagamento, totais, extras, ... }.
      // Aqui, capturamos ambos os formatos de maneira robusta para evitar o caso
      // observado nos logs onde "resumo" acabava sendo o wrapper com {status,data}
      // e, por isso, taxa e total eram zerados na normalização.
      const raw = (data?.data ?? data ?? {}) as Record<string, unknown>;
      const inner = (raw as any)?.data ?? {};
      const resumo = ((raw as any)?.resumo_pagamento ?? (inner as any)?.resumo_pagamento ?? raw) as import('../types/payment').ResumoPagamento;
      const totais = (((raw as any)?.totais ?? (inner as any)?.totais) ?? undefined) as import('../types/payment').TotaisResumo | undefined;
      const descontos = (((raw as any)?.descontos ?? (inner as any)?.descontos) ?? undefined) as Record<string, unknown> | undefined;
      const equipamentos = (((raw as any)?.equipamentos ?? (inner as any)?.equipamentos) ?? undefined) as Record<string, unknown> | undefined;
      const garantia = (((raw as any)?.garantia ?? (inner as any)?.garantia ?? (resumo as any)?.garantia) ?? undefined) as { valor?: number } | Record<string, unknown> | undefined;
      const payment = (((raw as any)?.payment ?? (inner as any)?.payment) ?? (body as any)?.payment ?? undefined) as { method?: 'pix'|'credit_card'; payment_method?: 'pix'|'credit_card'; installments?: number } | undefined;
      // Normalizar objeto de pagamento: garantir payment_method espelhado do method
      if (payment && !payment.payment_method && payment.method) {
        payment.payment_method = payment.method;
      }

      if ((import.meta as any)?.env?.DEV === true) {
        devLog('📥 Resposta cotação backend (raw)', raw);
        devLog('📥 Resposta cotação backend (normalizada)', resumo);
      }
      try {
        await fetch('client_log.php', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ level: 'info', event: 'QUOTE_RESPONSE', message: 'Resposta recebida', data: { raw, resumo } })
        });
      } catch {}

      if (!resumo || typeof resumo !== 'object') {
      devWarn('quote(): estrutura inesperada retornada pelo backend');
        try {
          await fetch('client_log.php', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ level: 'warn', event: 'QUOTE_STRUCTURE_UNEXPECTED', message: 'Resumo inválido', data })
          });
        } catch {}
        return { status: 'error', message: 'Erro ao calcular cotação' };
      }

      // {|INICIO| 22/07/2026 às 17:44 - ETAPA 6: FALHAR FECHADO SEM COMPONENTES FINANCEIROS OFICIAIS}
      const baseNorm = typeof resumo?.base_pagamento === 'number' && !Number.isNaN(resumo.base_pagamento)
        ? Number(resumo.base_pagamento.toFixed(2))
        : NaN;
      const totalNorm = typeof resumo?.total_a_pagar === 'number' && !Number.isNaN(resumo.total_a_pagar)
        ? Number(resumo.total_a_pagar.toFixed(2))
        : NaN;

      if (!Number.isFinite(baseNorm) || !Number.isFinite(totalNorm)) {
        devWarn('quote(): backend não retornou base/total oficiais válidos');
        return { status: 'error', message: 'Erro ao calcular cotação' };
      }

      const extrasObj = (resumo?.extras && typeof resumo.extras === 'object') ? resumo.extras : {} as import('../types/payment').ExtrasResumo;
      const taxaProc = typeof extrasObj?.taxa_processamento === 'number' && !Number.isNaN(extrasObj.taxa_processamento)
        ? Number(extrasObj.taxa_processamento.toFixed(2))
        : 0;
      const jurosParcelamento = typeof extrasObj?.juros_parcelamento === 'number' && !Number.isNaN(extrasObj.juros_parcelamento)
        ? Number(extrasObj.juros_parcelamento.toFixed(2))
        : 0;

      // Capturar garantia (quando presente no backend)
      const garantiaVal = typeof (garantia?.valor) === 'number'
        ? Number((garantia.valor as number).toFixed(2))
        : (typeof resumo?.garantia?.valor === 'number' ? Number(resumo.garantia.valor.toFixed(2)) : 0);

      // Normalizar taxa_meta e garantir repasse 'S' para PIX quando ausente
      const metaOrig = (extrasObj?.taxa_meta && typeof extrasObj.taxa_meta === 'object') ? extrasObj.taxa_meta : {} as import('../types/payment').TaxaMeta;
      const taxaMetaNorm: import('../types/payment').TaxaMeta = { ...metaOrig };
      const resumoNorm = {
        ...resumo,
        base_pagamento: baseNorm,
        garantia: { ...(resumo?.garantia || {}), valor: garantiaVal },
        extras: { ...extrasObj, taxa_processamento: taxaProc, juros_parcelamento: jurosParcelamento, taxa_meta: taxaMetaNorm },
        total_a_pagar: totalNorm
      } as import('../types/payment').ResumoPagamento;
      // {|FIM| 22/07/2026 às 17:44 - ETAPA 6: FALHAR FECHADO SEM COMPONENTES FINANCEIROS OFICIAIS}

      try {
        await fetch('client_log.php', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ level: 'info', event: 'QUOTE_NORMALIZED', message: 'Cotação normalizada', data: { base_pagamento: resumoNorm.base_pagamento, extras: resumoNorm.extras, totais: (totais ?? null), garantia: resumoNorm.garantia, total_a_pagar: resumoNorm.total_a_pagar } })
        });
      } catch {}

      return {
        status: 'success',
        data: {
          resumo_pagamento: resumoNorm,
          extras: resumoNorm.extras,
          totais,
          descontos,
          equipamentos,
          garantia,
          payment
        }
      };
    } catch (error) {
      devWarn('quote(): exceção na chamada ao backend', error);
      try {
        await fetch('client_log.php', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ level: 'error', event: 'QUOTE_EXCEPTION', message: String(error), data: {} })
        });
      } catch {}
      return { status: 'error', message: 'Erro ao calcular cotação' };
    }
  }

  static async tokenizeCard(cardData: {
    number: string;
    holder_name: string;
    exp_month: number;
    exp_year: number;
    cvv: string;
    holder_document?: string;
  }, options?: {
    codigoIbgePais?: number | string | null;
    tipoPessoa?: string | null;
  }) {
    const startTime = Date.now();
    const reservationId = `tokenize_${Date.now()}`;
    
    // Validar dados do cartão
    // {|INICIO| 07/10/2026 às 17:40 - PASSAPORTE: propagar codigoIbgePais/tipoPessoa na validação da tokenização para evitar fallback indevido em CPF/CNPJ}
    // Versão anterior (histórico): a tokenização validava sem contexto do cliente,
    // então o PaymentValidator caía no fallback brasileiro e rejeitava passaporte válido.
    // const validation = PaymentValidator.validateCard({
    //   number: cardData.number,
    //   holder_name: cardData.holder_name,
    //   exp_month: cardData.exp_month.toString(),
    //   exp_year: cardData.exp_year.toString(),
    //   cvv: cardData.cvv,
    //   holder_document: cardData.holder_document || ''
    // });
    const validation = PaymentValidator.validateCard({
      number: cardData.number,
      holder_name: cardData.holder_name,
      exp_month: cardData.exp_month.toString(),
      exp_year: cardData.exp_year.toString(),
      cvv: cardData.cvv,
      holder_document: cardData.holder_document || ''
    }, options);
    // {|FIM| 07/10/2026 às 17:40 - PASSAPORTE: propagar codigoIbgePais/tipoPessoa na validação da tokenização para evitar fallback indevido em CPF/CNPJ}
    
    // Log da validação
    PaymentLogger.logValidation({
      reservationId,
      validationType: 'CARD_VALIDATION',
      isValid: validation.valid,
      errors: validation.errors.map(e => e.message),
      warnings: validation.warnings.map(w => w.message),
      testCardDetected: !!validation.testCardInfo,
      testCardInfo: validation.testCardInfo
    });
    
    // Se a validação falhou, lançar erro
    if (!validation.valid) {
      const errorMessage = validation.errors.map(e => e.message).join(', ');
      PaymentLogger.logTokenization({
        reservationId,
        success: false,
        error: `Validação falhou: ${errorMessage}`,
        processingTime: Date.now() - startTime
      });
      
      throw new Error(`Dados do cartão inválidos: ${errorMessage}`);
    }
    
    // Verificar se o backend está disponível
    const backendAvailable = await this.isBackendAvailable();

    if (!backendAvailable) {
      devWarn('Backend não disponível para tokenizeCard');
      PaymentLogger.logTokenization({
        reservationId,
        success: false,
        error: 'Backend não disponível',
        processingTime: Date.now() - startTime
      });
      throw new Error('Erro ao tokenizar cartão');
    }

    // Preparar dados para tokenização
    const requestBody = {
      card_number: cardData.number.replace(/\s/g, ''),
      card_holder_name: cardData.holder_name.toUpperCase(),
      card_expiration_month: cardData.exp_month.toString().padStart(2, '0'),
      card_expiration_year: cardData.exp_year.toString(),
      card_cvv: cardData.cvv,
      card_holder_document: cardData.holder_document || ''
    };

    if ((import.meta as any)?.env?.DEV === true) {
      devLog('🔐 Tokenizando cartão:', {
        ...requestBody,
        card_number: '****' + requestBody.card_number.slice(-4),
        card_cvv: '***'
      });
    }

    const headers = {
      // {|INICIO| 29/09/2026 às 14:40 - REVERTER tokenizeCard PARA PADRAO BACKUP 25/09 (Basic AUTH_SEED, não montarCabecalhosAutenticados)}
      // Versao alterada nesta sessão (14:26h): usava montarCabecalhosAutenticados (incompativel com backup)
      /*
      ...this.montarCabecalhosAutenticados(true),
      */
      'Authorization': `Basic ${AUTH_SEED}`,
      'Content-Type': 'application/json',
      'Accept': 'application/json'
      // {|FIM| 29/09/2026 às 14:40 - REVERTER tokenizeCard PARA PADRAO BACKUP 25/09}
    };

    try {
      // {|INICIO| 10/05/2026 às 10:30 - COMPATIBILIDADE SAFARI/iOS: SUBSTITUIR AbortSignal.timeout POR fetchComTimeout}
      const response = await fetchComTimeout(API_ENDPOINTS.checkout.tokenizeCard, {
        method: 'POST',
        headers,
        body: JSON.stringify(requestBody),
      }, 15000); // 15 segundos para tokenização
      // {|FIM| 10/05/2026 às 10:30 - COMPATIBILIDADE SAFARI/iOS: SUBSTITUIR AbortSignal.timeout POR fetchComTimeout}

      if ((import.meta as any)?.env?.DEV === true) {
        devLog('📥 Resposta tokenização:', response.status, response.statusText);
      }

      const contentType = response.headers.get('content-type');

      // Falhar fechado quando o backend não devolver JSON válido
      if (!contentType?.includes('application/json')) {
        devWarn('Backend retornou HTML na tokenização');
        throw new Error('Erro ao tokenizar cartão');
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Erro ao tokenizar cartão');
      }

      const data = await response.json();
      
      const innerStatus = data?.data?.status ?? data?.status;
      const innerMessage = data?.data?.message ?? data?.message;
      if (innerStatus === 'error' || /Erro ao tokenizar cartão/i.test(innerMessage || '')) {
        devWarn('Backend retornou erro de tokenização:', innerMessage);
        PaymentLogger.logTokenization({
          reservationId,
          success: false,
          error: innerMessage || 'Erro ao tokenizar cartão (backend)',
          processingTime: Date.now() - startTime
        });
        throw new Error(innerMessage || 'Erro ao tokenizar cartão');
      }
      
      const extractToken = (payload: any): string | undefined => {
        if (!payload) return undefined;
        const candidates = [
          payload?.data?.card_token,
          payload?.card_token,
          payload?.data?.token,
          payload?.token,
          payload?.data?.id,
          payload?.id,
          payload?.data?.data?.card_token,
          payload?.data?.data?.id
        ];
        for (const c of candidates) {
          if (typeof c === 'string' && c.trim().length > 0) return c;
        }
        if (typeof payload?.data === 'string' && payload.data.trim().length > 0) {
          return payload.data;
        }
        return undefined;
      };

      const token = extractToken(data);
      if ((import.meta as any)?.env?.DEV === true) {
        devLog('✅ Token gerado:', token ? '****' + token.slice(-8) : 'N/A');
      }
      
      // Log da tokenização
      PaymentLogger.logTokenization({
        reservationId,
        success: !!token,
        cardBrand: data.data?.card_brand,
        cardLastFour: cardData.number.slice(-4),
        error: token ? undefined : 'Resposta sem token válido',
        processingTime: Date.now() - startTime
      });
      
      return {
        id: token,
        status: data?.status ?? 'success',
        data: data?.data ?? { card_token: token }
      };
    } catch (error) {
      devError('❌ Erro na tokenização:', error);
      
      // Log do erro
      PaymentLogger.logTokenization({
        reservationId,
        success: false,
        error: error instanceof Error ? error.message : 'Erro desconhecido',
        processingTime: Date.now() - startTime
      });
      
      throw new Error(error instanceof Error ? error.message : 'Erro ao tokenizar cartão');
    }
  }

  static async getPaymentStatus(chargeId: string): Promise<string> {
    const backendAvailable = await this.isBackendAvailable();

    if (!backendAvailable) {
      devWarn('Backend não disponível, usando MOCK para status');
      return CheckoutMock.getPaymentStatus(chargeId);
    }

    const headers = this.montarCabecalhosAutenticados(true);

    try {
      // Escolher a chave mais apropriada com base no formato do identificador
      const classifyKey = (id: string): 'charge_id' | 'order_id' | 'idreserva' => {
        const s = String(id).trim();
        if (/^ch_/.test(s)) return 'charge_id';
        if (/^or_/.test(s)) return 'order_id';
        if (/^\d+$/.test(s)) return 'idreserva';
        return 'charge_id';
      };
      const primaryKey = classifyKey(chargeId);
      const keys: Array<'charge_id' | 'order_id' | 'idreserva'> = [primaryKey, ...(['charge_id', 'order_id', 'idreserva'] as const).filter(k => k !== primaryKey)];

      for (const key of keys) {
        const endpoint = `${API_ENDPOINTS.checkout.getPaymentStatus}?${key}=${encodeURIComponent(chargeId)}`;
        if ((import.meta as any)?.env?.DEV === true) {
          devLog(`🔎 Consultando status do pagamento (${key}):`, chargeId, '->', endpoint);
        }
        // {|INICIO| 10/05/2026 às 10:30 - COMPATIBILIDADE SAFARI/iOS: SUBSTITUIR AbortSignal.timeout POR fetchComTimeout}
        const response = await fetchComTimeout(endpoint, {
          method: 'GET',
          headers,
        }, 6000);
        // {|FIM| 10/05/2026 às 10:30 - COMPATIBILIDADE SAFARI/iOS: SUBSTITUIR AbortSignal.timeout POR fetchComTimeout}

        let data: any = null;
        try {
          // Primeiro tenta JSON direto
          data = await response.json();
        } catch {
          // Fallback: tentar extrair JSON de resposta textual/HTML
          try {
            const text = await response.text();
            const match = text.match(/\{[\s\S]*\}/);
            data = match ? JSON.parse(match[0]) : null;
          } catch {
            data = null;
          }
        }
        if ((import.meta as any)?.env?.DEV === true) {
          devLog('📥 Resposta checkoutStatus:', data);
        }

        const extractStatus = (payload: any): string => {
          if (!payload) return 'pending';
          const candidates = [
            payload?.status,
            payload?.data?.status,
            payload?.data?.charge_status,
            payload?.charge_status,
            payload?.data?.data?.status,
            payload?.data?.data?.charge_status,
            payload?.transaction?.status,
            payload?.status_pagamento,
            payload?.data?.status_pagamento
          ];
          for (const s of candidates) {
            if (typeof s === 'string') {
              const normalized = s.toLowerCase();
              // Somente status de transação reais devem ser mapeados para 'paid'
              if (normalized === 'paid' || normalized === 'captured' || normalized === 'pago') return 'paid';
              // 'success' aqui costuma ser envelope do backend, não status de transação
              if (['failed', 'error', 'canceled', 'cancelled', 'expired', 'not_authorized'].includes(normalized)) return 'failed';
              if (['pending', 'processing', 'waiting', 'authorized'].includes(normalized)) return normalized;
            }
          }
          return 'pending';
        };

        const status = extractStatus(data);
        // Se obtivermos uma resposta válida de qualquer chave, retornar imediatamente
        if (status !== 'pending' || response.ok) {
          return status;
        }
      }

      devWarn('checkoutStatus: nenhuma resposta JSON válida para charge_id/order_id/idreserva');
      return 'pending';
    } catch (error) {
      devWarn('Erro na comunicação com backend:', error);
      // Em caso de erro, assumir que está processando
      return 'processing';
    }
  }

  /**
   * Obtém detalhes do pagamento (incluindo possíveis dados PIX) a partir do backend.
   * Tenta consultar usando diferentes identificadores suportados (charge_id, order_id, idreserva).
   * Retorna objeto leve com campos relevantes quando disponíveis.
   */
  static async getPaymentInfo(params: { chargeId?: string; orderId?: string; idreserva?: number | string }): Promise<{ pix?: import('../types/payment').PixInfo | undefined; transaction?: import('../types/payment').TransactionInfo | undefined; charge_id?: string; order_id?: string; status?: string } | null> {
    try {
      /**
       * Busca status/QR de pagamento de forma CONCORRENTE e com timeout curto.
       * Objetivo: reduzir latência na verificação de reuso do QR Code PIX.
       * - Faz consultas em paralelo para `charge_id`, `order_id` e `idreserva` (quando disponíveis).
       * - Usa timeout de 2s por requisição para evitar travar a UI em etapas de verificação.
       */
      const headers = this.montarCabecalhosAutenticados(false);
      const candidates: Array<{ key: string; value: string }> = [];
      if (params.chargeId) candidates.push({ key: 'charge_id', value: String(params.chargeId) });
      if (params.orderId) candidates.push({ key: 'order_id', value: String(params.orderId) });
      if (params.idreserva != null) candidates.push({ key: 'idreserva', value: String(params.idreserva) });

      if (candidates.length === 0) return null;

      // Aumenta o timeout para ambientes de produção com latência maior
      const TIMEOUT_MS = 6000;

      const tasks = candidates.map(async (cand) => {
        try {
          const url = `${API_ENDPOINTS.checkout.getPaymentStatus}?${cand.key}=${encodeURIComponent(cand.value)}`;
          // {|INICIO| 10/05/2026 às 10:30 - COMPATIBILIDADE SAFARI/iOS: SUBSTITUIR AbortSignal.timeout POR fetchComTimeout}
          const resp = await fetchComTimeout(url, { method: 'GET', headers }, TIMEOUT_MS);
          // {|FIM| 10/05/2026 às 10:30 - COMPATIBILIDADE SAFARI/iOS: SUBSTITUIR AbortSignal.timeout POR fetchComTimeout}
          if (!resp.ok) return null;
          // Tentar JSON direto; se falhar, tentar parse a partir de texto (alguns ambientes retornam HTML com JSON embutido)
          let data: any = null;
          try {
            data = await resp.json();
          } catch {
            try {
              const text = await resp.text();
              const match = text.match(/\{[\s\S]*\}/);
              data = match ? JSON.parse(match[0]) : null;
            } catch {
              data = null;
            }
          }
          const root = (data ?? {}) as Record<string, unknown>;
          const outer = (typeof root['data'] === 'object' && root['data'] !== null) ? (root['data'] as Record<string, unknown>) : root;
          const innerData = (typeof outer['data'] === 'object' && outer['data'] !== null) ? (outer['data'] as Record<string, unknown>) : undefined;
          const innerRootData = (typeof root['data'] === 'object' && root['data'] !== null) ? (root['data'] as Record<string, unknown>) : undefined;
          const innerRootDeep = (typeof (innerRootData as any)?.['data'] === 'object' && (innerRootData as any)['data'] !== null) ? ((innerRootData as any)['data'] as Record<string, unknown>) : undefined;
          const paymentObj = (typeof outer['payment'] === 'object' && outer['payment'] !== null) ? (outer['payment'] as Record<string, unknown>) : (typeof innerData?.['payment'] === 'object' ? (innerData?.['payment'] as Record<string, unknown>) : undefined);
          const transactionObj = (typeof outer['transaction'] === 'object' && outer['transaction'] !== null)
            ? (outer['transaction'] as Record<string, unknown>)
            : (typeof (paymentObj?.['transaction']) === 'object' && (paymentObj?.['transaction']) !== null)
              ? (paymentObj?.['transaction'] as Record<string, unknown>)
              : (typeof innerData?.['transaction'] === 'object' && innerData?.['transaction'] !== null)
                ? (innerData?.['transaction'] as Record<string, unknown>)
              : undefined;

          // Helper seguro de leitura de string
          const readStr = (obj: Record<string, unknown> | undefined, key: string): string | undefined => {
            if (!obj) return undefined;
            const v = obj[key];
            return typeof v === 'string' ? v : undefined;
          };

          // Resolve PIX a partir de diferentes formatos/sinônimos
          const resolvePix = (obj: Record<string, unknown> | undefined): import('../types/payment').PixInfo | undefined => {
            if (!obj) return undefined;
            const qrCode = readStr(obj, 'qr_code')
              ?? readStr(obj, 'qrCode')
              ?? readStr(obj, 'qrcode')
              ?? readStr(obj, 'qr_image')
              ?? readStr(obj, 'qr')
              ?? readStr(obj, 'imagem_qrcode');
            const qrText = readStr(obj, 'qr_code_text')
              ?? readStr(obj, 'qrCodeText')
              ?? readStr(obj, 'qrcode_text')
              ?? readStr(obj, 'qrcodeText')
              ?? readStr(obj, 'qr_text');
            const qrUrl = readStr(obj, 'qr_code_url')
              ?? readStr(obj, 'qrCodeUrl');
            const expires = readStr(obj, 'expires_at')
              ?? readStr(obj, 'expiresAt')
              ?? readStr(obj, 'expira_em');
            if (qrCode || qrText || qrUrl) {
              return {
                qr_code: String(qrCode ?? ''),
                qr_code_text: String((qrText ?? qrCode ?? '')),
                qr_code_url: String(qrUrl ?? ''),
                expires_at: String(expires ?? '')
              };
            }
            return undefined;
          };

          // Tentar resolver PIX de múltiplas fontes, incluindo níveis internos comuns
          const pixResolved = resolvePix(outer['pix'] as Record<string, unknown> | undefined)
            ?? resolvePix(paymentObj?.['pix'] as Record<string, unknown> | undefined)
            ?? resolvePix(transactionObj)
            ?? resolvePix(innerData?.['pix'] as Record<string, unknown> | undefined)
            ?? resolvePix((innerData as any)?.['payment'] as Record<string, unknown> | undefined)
            ?? resolvePix((innerData as any)?.['transaction'] as Record<string, unknown> | undefined)
            ?? resolvePix(innerRootDeep?.['pix'] as Record<string, unknown> | undefined)
            ?? resolvePix(outer)
            ?? resolvePix(innerData)
            ?? resolvePix(innerRootData)
            ?? resolvePix(innerRootDeep)
            ?? resolvePix(root);

          const chargeId = readStr(outer, 'charge_id')
            ?? readStr(paymentObj, 'charge_id')
            ?? readStr(transactionObj, 'id')
            ?? readStr(innerData, 'charge_id')
            ?? readStr((innerData as any)?.['transaction'] as Record<string, unknown> | undefined, 'id')
            ?? readStr(innerRootDeep, 'charge_id')
            ?? readStr(root, 'charge_id');
          const orderId = readStr(outer, 'order_id')
            ?? readStr(paymentObj, 'order_id')
            ?? readStr(innerData, 'order_id')
            ?? readStr(innerRootDeep, 'order_id')
            ?? readStr(root, 'order_id');
          const status = readStr(outer, 'status')
            ?? readStr(outer, 'charge_status')
            ?? readStr(paymentObj, 'status')
            ?? readStr(paymentObj, 'charge_status')
            ?? readStr(innerData, 'status')
            ?? readStr((innerData as any), 'charge_status')
            ?? readStr(innerRootDeep, 'status')
            ?? readStr((innerRootDeep as any), 'charge_status')
            ?? readStr(root, 'status');

          const out: { pix?: import('../types/payment').PixInfo; transaction?: import('../types/payment').TransactionInfo; charge_id?: string; order_id?: string; status?: string } = {
            pix: pixResolved,
            transaction: (transactionObj as unknown) as import('../types/payment').TransactionInfo | undefined,
            charge_id: chargeId,
            order_id: orderId,
            status: status
          };

          return (out.pix || out.transaction || out.charge_id || out.order_id) ? out : null;
        } catch {
          return null;
        }
      });

      const results = await Promise.allSettled(tasks);
      for (const r of results) {
        if (r.status === 'fulfilled' && r.value) {
          return r.value;
        }
      }
      return null;
    } catch {
      return null;
    }
  }

  /**
   * Consulta status do checkout por reserva, retornando formato amigável
   * com campos para reuso direto do QR Code (PIX) quando aplicável.
   */
  static async getCheckoutStatusPorReserva(idreserva: number | string): Promise<{
    idreserva: number;
    charge_id?: string;
    order_id?: string;
    status_pagamento?: string;
    tem_pix_pendente: boolean;
    pix_status?: string;
    pix_qrcode?: string;
    pix_qrcode_text?: string;
    pix_qrcode_url?: string;
    pix_expires_at?: string;
    total_inclui_garantia?: boolean;
    garantia_valor?: number;
    resumo_pagamento?: any;
  } | null> {
    try {
      const postUrl = API_ENDPOINTS.checkout.getPaymentStatusByReservation;
      const getUrl = `${API_ENDPOINTS.checkout.getPaymentStatusByReservation}?idreserva=${encodeURIComponent(String(idreserva))}`;
      const headers = this.montarCabecalhosAutenticados(true);

      // Tentar primeiro via POST com JSON
      logNetworkRequest({ endpoint: 'getCheckoutStatusPorReserva', method: 'POST', url: postUrl, payload: { idreserva }, headers: { Authorization: headers.Authorization ? 'Bearer/Basic ****' : '', Accept: headers.Accept, 'Content-Type': headers['Content-Type'] } });
      if (!headers.Authorization) {
        return {
          idreserva: Number(idreserva),
          status_pagamento: 'not_authorized',
          tem_pix_pendente: false,
        };
      }
      // {|INICIO| 10/05/2026 às 10:30 - COMPATIBILIDADE SAFARI/iOS: SUBSTITUIR AbortSignal.timeout POR fetchComTimeout}
      let resp = await fetchComTimeout(postUrl, { method: 'POST', headers, body: JSON.stringify({ idreserva }) }, 8000);
      // {|FIM| 10/05/2026 às 10:30 - COMPATIBILIDADE SAFARI/iOS: SUBSTITUIR AbortSignal.timeout POR fetchComTimeout}
      logNetworkResponse({ endpoint: 'getCheckoutStatusPorReserva', method: 'POST', status: resp.status });

      // Fallback para GET com querystring quando POST não for aceito
      if (!resp.ok) {
        logNetworkRequest({ endpoint: 'getCheckoutStatusPorReserva', method: 'GET', url: getUrl, headers: { Authorization: headers.Authorization ? 'Bearer/Basic ****' : '', Accept: headers.Accept } });
        // {|INICIO| 10/05/2026 às 10:30 - COMPATIBILIDADE SAFARI/iOS: SUBSTITUIR AbortSignal.timeout POR fetchComTimeout}
        resp = await fetchComTimeout(getUrl, { method: 'GET', headers: { 'Authorization': headers['Authorization'], 'Accept': headers['Accept'] } }, 8000);
        // {|FIM| 10/05/2026 às 10:30 - COMPATIBILIDADE SAFARI/iOS: SUBSTITUIR AbortSignal.timeout POR fetchComTimeout}
        logNetworkResponse({ endpoint: 'getCheckoutStatusPorReserva', method: 'GET', status: resp.status });
      }

      if (!resp.ok) {
        let bodyText = '';
        try { bodyText = await resp.text(); } catch {}
        const unauthorized = /erro de autorizau000063u0000e7u0000e3o|erro de autorização|not_authorized|unauthorized/i.test(bodyText);
        if (unauthorized) {
          return {
            idreserva: Number(idreserva),
            status_pagamento: 'not_authorized',
            tem_pix_pendente: false,
          };
        }
        return null;
      }
      // Function-level comment: Resposta pode vir com múltiplos envelopes `data`.
      // Ex.: { status, data: { status, data: { ...campos } } }
      // Abaixo normalizamos percorrendo até dois níveis de `data`.
      let data: any = null;
      try {
        data = await resp.json();
      } catch {
        const text = await resp.text();
        const match = text.match(/\{[\s\S]*\}/);
        data = match ? JSON.parse(match[0]) : null;
      }
      const root = (data ?? {}) as Record<string, unknown>;
      const outer = (typeof root['data'] === 'object' && root['data'] !== null)
        ? (root['data'] as Record<string, unknown>)
        : root;
      const inner = (typeof outer['data'] === 'object' && outer['data'] !== null)
        ? (outer['data'] as Record<string, unknown>)
        : undefined;

      const source = inner ?? outer;
      const readStr = (obj: Record<string, unknown> | undefined, key: string): string | undefined => {
        if (!obj) return undefined;
        const v = obj[key];
        return typeof v === 'string' ? v : undefined;
      };
      const readBool = (obj: Record<string, unknown> | undefined, key: string, def = false): boolean => {
        if (!obj) return def;
        const v = obj[key];
        if (typeof v === 'boolean') return v;
        if (typeof v === 'number') return v !== 0;
        if (typeof v === 'string') {
          const s = v.trim().toLowerCase();
          return ['true','1','yes','sim'].includes(s);
        }
        return def;
      };

      const out = {
        idreserva: Number((source?.['idreserva'] as any) ?? idreserva),
        charge_id: readStr(source, 'charge_id'),
        order_id: readStr(source, 'order_id'),
        status_pagamento: readStr(source, 'status_pagamento'),
        tem_pix_pendente: readBool(source, 'tem_pix_pendente', false),
        pix_status: readStr(source, 'pix_status'),
        pix_qrcode: readStr(source, 'pix_qrcode'),
        pix_qrcode_text: readStr(source, 'pix_qrcode_text'),
        pix_qrcode_url: readStr(source, 'pix_qrcode_url'),
        pix_expires_at: readStr(source, 'pix_expires_at'),
        total_inclui_garantia: readBool(source, 'total_inclui_garantia', false),
        garantia_valor: (() => {
          const gv = (source?.['garantia_valor'] as any);
          return typeof gv === 'number' ? gv : undefined;
        })(),
        resumo_pagamento: (typeof source?.['resumo_pagamento'] === 'object' && source?.['resumo_pagamento'] !== null)
          ? (source?.['resumo_pagamento'] as any)
          : undefined,
      };
      return out;
    } catch {
      return null;
    }
  }

  /**
   * Atualiza/Grava o status do pagamento no backend via endpoint `checkoutStatus`.
   * Preferência: POST com `idreserva`, `charge_id`/`order_id` e `status`.
   * Em caso de falha de POST, tenta GET com querystring para compatibilidade.
   */
  static async updatePaymentStatus(payload: { idreserva: number | string; chargeId?: string; orderId?: string; status: string }): Promise<boolean> {
    try {
      const headers = {
        'Authorization': `Basic ${AUTH_SEED}`,
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      };

      const body = {
        idreserva: String(payload.idreserva),
        charge_id: payload.chargeId ? String(payload.chargeId) : undefined,
        order_id: payload.orderId ? String(payload.orderId) : undefined,
        status: String(payload.status).toLowerCase()
      };

      // {|INICIO| 10/05/2026 às 10:30 - COMPATIBILIDADE SAFARI/iOS: SUBSTITUIR AbortSignal.timeout POR fetchComTimeout}
      const postResp = await fetchComTimeout(API_ENDPOINTS.checkout.getPaymentStatus, {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
      }, 6000);
      // {|FIM| 10/05/2026 às 10:30 - COMPATIBILIDADE SAFARI/iOS: SUBSTITUIR AbortSignal.timeout POR fetchComTimeout}

      if (postResp.ok) {
        return true;
      }

      // Fallback GET: alguns ambientes aceitam atualização via querystring
      const qs = new URLSearchParams();
      qs.set('idreserva', String(payload.idreserva));
      if (payload.chargeId) qs.set('charge_id', String(payload.chargeId));
      if (payload.orderId) qs.set('order_id', String(payload.orderId));
      qs.set('status', String(payload.status).toLowerCase());

      const getUrl = `${API_ENDPOINTS.checkout.getPaymentStatus}?${qs.toString()}`;
      // {|INICIO| 10/05/2026 às 10:30 - COMPATIBILIDADE SAFARI/iOS: SUBSTITUIR AbortSignal.timeout POR fetchComTimeout}
      const getResp = await fetchComTimeout(getUrl, { method: 'GET', headers: { 'Authorization': `Basic ${AUTH_SEED}`, 'Accept': 'application/json' } }, 6000);
      // {|FIM| 10/05/2026 às 10:30 - COMPATIBILIDADE SAFARI/iOS: SUBSTITUIR AbortSignal.timeout POR fetchComTimeout}
      return getResp.ok;
    } catch {
      return false;
    }
  }

  static async processPayment(paymentData: {
    slotId: string;
    userId: number;
    paymentMethod: 'pix' | 'credit_card';
    totalValue: number;
    reservationType: string;
    quantity: number;
    cardToken?: string;
  }): Promise<{ success: boolean; data?: CreateCheckoutResponse['data']; message?: string; status?: CreateCheckoutResponse['status'] }> {
    try {
      PaymentLogger.log('ProcessPayment iniciado', { paymentData });

      const createRequest: CreateCheckoutRequest = {
        id_cliente: paymentData.userId,
        idreserva: parseInt(paymentData.slotId),
        method_payment: paymentData.paymentMethod,
        valor_total: paymentData.totalValue,
        tipo_reserva: paymentData.reservationType,
        card_token: paymentData.cardToken,
      };

      const response = await this.createPayment(createRequest);
      PaymentLogger.log('ProcessPayment resposta recebida', { response });

      if (response.status === 'success') {
        return {
          success: true,
          data: response.data,
          status: response.status,
        };
      } else {
        return {
          success: false,
          message: response.message || 'Erro no processamento do pagamento',
          status: response.status,
        };
      }
    } catch (error) {
      PaymentLogger.error('Erro em processPayment', error);
      return {
        success: false,
        message: 'Erro interno no processamento do pagamento',
      };
    }
  }

  /**
   * Obtém opções de parcelamento otimizadas (1x até 12x) em uma única chamada.
   * Substitui múltiplas chamadas ao endpoint quote por uma única requisição.
   */
  // {|INICIO| 02/04/2026 às 09:42 - REGRA 9: RENOMEAR FUNÇÃO PARA PT-BR}
  static async obterOpcoesParcelamento(request: InstallmentOptionsRequest): Promise<InstallmentOptionsResponse> {
    try {
      PaymentLogger.log('InstallmentOptions iniciado', { request });

      if (!(await this.isBackendAvailable())) {
        throw new Error('Backend indisponível');
      }
      // Usar endpoint configurado com cabeçalho de autenticação, alinhado com a rota de quote
      const url = API_ENDPOINTS.checkout.installmentOptions;

      // Construir payload garantindo o uso de `payment_method` em vez de `method`
      const payload: any = { ...request };
      // Compatibilidade: se existir `method` oriundo de código legado, converte para `payment_method`
      if ((payload as any).method && !payload.payment_method) {
        payload.payment_method = (payload as any).method;
      }
      // Alias comumente esperado pelos serviços PHP
      if (!payload['method_payment'] && payload.payment_method) {
        (payload as any).method_payment = payload.payment_method;
      }
      // Evita enviar `method` no corpo para não confundir roteamento do backend
      delete (payload as any).method;
      // Remover max_parcelas do payload de POST: backend define limites a partir do banco
      if (payload.max_parcelas !== undefined) {
        delete (payload as any).max_parcelas;
      }

      // Log da requisição POST
      logNetworkRequest({ endpoint: 'installmentOptions', method: 'POST', url: url, payload: payload, headers: {
        'Authorization': `Basic ${AUTH_SEED}`,
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      } });

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Basic ${AUTH_SEED}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(payload),
        // Timeout curto para evitar travas caso o endpoint esteja indisponível
        signal: (AbortSignal as any).timeout ? (AbortSignal as any).timeout(8000) : undefined
      });

      // Parse da resposta em JSON quando OK; caso contrário, tentar GET fallback abaixo
      let rawData: any = undefined;
      if (response.ok) {
        rawData = await response.json();
        logNetworkResponse({ endpoint: 'installmentOptions', method: 'POST', url, status: response.status, body: rawData });
      } else {
        // Aviso explícito quando POST falha e vamos tentar o fallback GET
        PaymentLogger.warn('InstallmentOptions POST falhou, tentando GET fallback', { status: response.status, statusText: response.statusText });
        logNetworkError({ endpoint: 'installmentOptions', method: 'POST', url, status: response.status, error: new Error(response.statusText || `HTTP ${response.status}`) });
      }
      // Log seguro usando o objeto correto
      PaymentLogger.log('InstallmentOptions resposta recebida', { data: rawData });

      // Normalizar a estrutura aninhada, como observado nos logs
      // Alguns backends retornam { status, data: { data: { ... } } }
      // Para evitar perda de campos, sempre colapsamos em um único nível.
      const normalizedData = rawData?.data?.data ? rawData.data : rawData;
      const inner = normalizedData?.data?.data ? normalizedData.data.data : normalizedData?.data;
      if (inner && !normalizedData.data?.options && Array.isArray(inner.options)) {
        // Se options está em data.data, promover para data
        normalizedData.data = inner;
      }

      // Garantir que payment_method esteja presente se method existir
      if (normalizedData?.data?.method && !normalizedData.data.payment_method) {
        normalizedData.data.payment_method = normalizedData.data.method;
      }

      // Se POST não retornou dados válidos ou options vazias, tentar GET fallback
      const hasValidOptions = Array.isArray(normalizedData?.data?.options) && normalizedData.data.options.length > 0;
      if (!hasValidOptions) {
        try {
          const qs = new URLSearchParams();
          if (typeof payload.idreserva === 'number') qs.set('idreserva', String(payload.idreserva));
          if (typeof payload.id_cliente === 'number') qs.set('idcliente', String(payload.id_cliente));
          if (typeof payload.payment_method === 'string') qs.set('payment_method', String(payload.payment_method));
          if (typeof payload.tipo_reserva === 'string') qs.set('tipo_reserva', String(payload.tipo_reserva));
          // Removido: max_parcelas não deve ser controlado pelo frontend; o backend decide as parcelas ativas
          if (typeof payload.valor_total === 'number') qs.set('valor_total', String(payload.valor_total));
          if (typeof payload.qtdpessoas === 'number') qs.set('qtdpessoas', String(payload.qtdpessoas));
          if (typeof payload.valor_unitario === 'number') qs.set('valor_unitario', String(payload.valor_unitario));
          if (typeof payload.voucher === 'string' && payload.voucher) qs.set('voucher', String(payload.voucher));

          const getUrl = `${url}?${qs.toString()}`;
          // Log da requisição GET fallback
          logNetworkRequest({ endpoint: 'installmentOptions', method: 'GET', url: getUrl, payload: undefined, headers: {
            'Authorization': `Basic ${AUTH_SEED}`,
            'Accept': 'application/json',
          } });
          const getResp = await fetch(getUrl, {
            method: 'GET',
            headers: {
              'Authorization': `Basic ${AUTH_SEED}`,
              'Accept': 'application/json',
            },
            signal: (AbortSignal as any).timeout ? (AbortSignal as any).timeout(8000) : undefined
          });
          if (getResp.ok) {
            const getData = await getResp.json();
            PaymentLogger.log('InstallmentOptions GET resposta recebida', { data: getData });
            logNetworkResponse({ endpoint: 'installmentOptions', method: 'GET', url: getUrl, status: getResp.status, body: getData });
            const normalizedGet = getData?.data?.data ? getData.data : getData;
            const innerGet = normalizedGet?.data?.data ? normalizedGet.data.data : normalizedGet?.data;
            if (innerGet && !normalizedGet.data?.options && Array.isArray(innerGet.options)) {
              normalizedGet.data = innerGet;
            }
            if (normalizedGet?.data?.method && !normalizedGet.data.payment_method) {
              normalizedGet.data.payment_method = normalizedGet.data.method;
            }
            return normalizedGet;
          } else {
            // Aviso explícito quando GET fallback falha
            PaymentLogger.warn('InstallmentOptions GET fallback falhou', { status: getResp.status, statusText: getResp.statusText });
            logNetworkError({ endpoint: 'installmentOptions', method: 'GET', url: getUrl, status: getResp.status, error: new Error(getResp.statusText || `HTTP ${getResp.status}`) });
          }
        } catch (erro) {
          PaymentLogger.error('Erro em GET fallback installmentOptions', erro);
        }
      }

      return normalizedData;
    } catch (error) {
      PaymentLogger.error('Erro em installmentOptions', error);
      return {
        status: 'error',
        message: error instanceof Error ? error.message : 'Erro desconhecido',
      };
    }
  }
  // {|FIM| 02/04/2026 às 09:42 - REGRA 9: RENOMEAR FUNÇÃO PARA PT-BR}

  // {|INICIO| 02/04/2026 às 09:42 - COMPATIBILIDADE: MANTER ASSINATURA ANTIGA}
  static async installmentOptions(request: InstallmentOptionsRequest): Promise<InstallmentOptionsResponse> {
    return this.obterOpcoesParcelamento(request);
  }
  // {|FIM| 02/04/2026 às 09:42 - COMPATIBILIDADE: MANTER ASSINATURA ANTIGA}
}
// {|FIM| 02/04/2026 às 10:05 - AJUSTES PARA LINT: REMOVER IMPORTS/VARIÁVEIS NÃO UTILIZADOS E PADRONIZAR CATCH}
